import React from 'react';
import { renderToString } from 'react-dom/server';
import { useNavigate } from 'react-router-dom';

import Tabs, { TabsProps } from 'antd/es/tabs';
import Modal from 'antd/es/modal';

import GoogleMap from '@common/react/components/google/GoogleMap/GoogleMap';

import { numberWithComma } from '@commonTuna/react/utils/NumberWithComma/NumberWithComma';

import { LocationPortal } from '@app/objects/CompanyPortal';
import { Location } from '@app/objects/Location';
import WithPathLink from '@app/components/UI/WithPathLink/WithPathLink';
import { getLocationName } from '@app/utils/Utils';
import Stars from '@app/components/UI/Stars/Stars';
import LocationInfo from '@app/components/Pages/Doctor/LocationInfo';
import Rate from '@app/components/Pages/Doctor/Rate';

import '@app/scss/components/locations.scss';

interface IPros {
	locations: Array<LocationPortal>;
	containerClassName?: string;
	content?: (location: LocationPortal) => JSX.Element;
	reviews?: (location: LocationPortal) => React.ReactNode;
	hideTitle?: boolean;
	hideLink?: boolean;
}

interface MapProps {
	address: string;
	addressList?: Array<string | Location>;
}

interface LocationNodeProps {
	content: any;
	reviews?: (location: LocationPortal) => React.ReactNode;
	item: any;
	hideLink?: boolean;
	hideMap?: boolean;
	addressAsLink?: boolean;
	showRate?: boolean;
}

export const getAddress = (item) => {
	return [item.addressEn, item.city, item.state, item.zip].filter((e) => e).join(', ');
};

const getClinicAvatar = (location: Location): string => {
	if (location.portalOriginalAvatar) {
		return `/remote/${location.portalOriginalAvatar}`;
	}
	if (location.portalAvatar) {
		return `/remote/${location.portalAvatar}`;
	}
	return '';
};

function codeAddress(map, maps, address: string, addressList?: Array<string | Location>, onLoad?: (address, result, openPopup, callback) => void) {
	const geocoder = new maps.Geocoder();
	const gedData = (item: string | Location) => {
		const isLocation = typeof item !== 'string';
		const currentAddress = !isLocation ? item : getAddress(item);
		geocoder.geocode({ address: currentAddress }, (results, status) => {
			if (status === 'OK') {
				if (address === currentAddress) {
					map.setCenter(results[0].geometry.location);
				}
				// eslint-disable-next-line
				const marker = new maps.Marker({
					map,
					position: results[0].geometry.location,
					clickable: true,
				});
				if (isLocation) {
					const locationName = getLocationName(item, false, true);
					const avatar = getClinicAvatar(item);
					const infowindow = new google.maps.InfoWindow({
						content: `${isLocation ? renderToString(<div className="location-map-info">
							<h4>
								<a style={{ cursor: 'pointer' }} data-link={`/clinics/${item.portalPathEn}`}>{locationName}</a>
							</h4>
							{avatar ? <div className="location-map-info-avatar">
								<img
									src={avatar}
									alt={locationName as string}
								/>
							</div> : ''}
							<Rate
								averageReviewRate={item.averageReviewRate || 0}
								totalReviewCount={item.totalReviewCount}
							/>
							<LocationNode
								hideMap
								hideLink
								content=""
								addressAsLink
								item={item}
							/>
							{item.portalDescription
								&& <div
									className="mt10"
									dangerouslySetInnerHTML={{ __html: item.portalDescription }}
								/>
							}
						</div>) : currentAddress}`,
						ariaLabel: '',
					});
					const onClick = () => {
						infowindow.open({
							anchor: marker,
							map,
						});
					};
					marker.addListener('click', onClick);
					if (address === currentAddress) {
						onClick();
					}
					onLoad && onLoad(currentAddress, results[0].geometry.location, {
						open: onClick,
						close: () => {
							infowindow.close();
						},
					}, () => {
						marker.removeListener('click', onClick);
					});
				} else {
					onLoad && onLoad(currentAddress, results[0].geometry.location, undefined, () => undefined);
				}
			} else {
				console.log(`Geocode was not successful for the following reason: ${status}`);
			}
		});
	};
	const list: any = addressList || [];
	(list?.map((item) => (typeof item === 'string' ? item : getAddress(item))).includes(address) ? list : address ? [address].concat(list) : list)
		.forEach(gedData);
}

export const SimpleMap: React.FC<MapProps> = ({ address, addressList }) => {
	const navigate = useNavigate();
	const listenersRef = React.useRef<any>([]);
	const addressRef = React.useRef<any>({});
	const popupRef = React.useRef<any>({});

	React.useEffect(() => {
		if (listenersRef.current.length) {
			return () => listenersRef.current.forEach((callback) => callback());
		}
	}, []);

	const renderMarker = (map, maps) => {
		if (addressRef.current[address]) {
			map.setCenter(addressRef.current[address]);
			if (popupRef.current[address]) {
				Object.values(popupRef.current).forEach((item: any) => {
					item?.close();
				});
				popupRef.current[address]?.open();
			}
		} else {
			codeAddress(map, maps, address);
		}
	};

	return <GoogleMap
		address={address}
		className="location-map"
		onClick={(e) => {
			const target: any = e.event.target;
			if (target && target.dataset.link) {
				e.event.preventDefault();
				navigate(target.dataset.link);
			}
		}}
		withoutIntersectionObserver
		onGoogleApiLoaded={(map, maps) => {
			codeAddress(map, maps, address, addressList, (address, result, openPopup, callback) => {
				listenersRef.current.concat(callback);
				addressRef.current[address] = result;
				popupRef.current[address] = openPopup;
			});
		}}
		renderMarker={renderMarker}
	/>;
};

interface ILocationModal {
	location: Location;
	buttonClassName?: string;
	buttonValue?: JSX.Element;
}

export const LocationModal: React.FC<ILocationModal> = (props) => {
	const [visible, setVisible] = React.useState<boolean>(false);
	const { location } = props;

	const close = () => setVisible(false);

	const open = () => setVisible(true);
	const locationName = getLocationName(location, false, true);
	const {
		buttonValue = <>
			<i className="fa fa-map-marker" />
			{' '}
			{locationName}
		</>,
		buttonClassName,
	} = props;

	return (
		<>
			<button type="button" onClick={open} className={`btn ${buttonClassName || ''}`}>
				{buttonValue}
			</button>
			<Modal
				open={visible}
				onCancel={close}
				title={<div>
					<WithPathLink prefix="clinics" path={location.portalPathEn}>
						{locationName}
					</WithPathLink>
					<div className="stars pull-right" style={{ color: 'rgba(0, 0, 0, 0.85)', marginRight: 25 }}>
						<strong className="mr10" style={{ color: '#f60' }}>
							<Stars value={location.averageReviewRate < 1 ? 1 : location.averageReviewRate || 0} />
						</strong>
						{numberWithComma(location.averageReviewRate || 0)}
						{' '}
						(
						{location.totalReviewCount || 0}
						)
					</div>
				</div>}
				width={600}
				className="location-popap"
				footer={null}
			>
				<LocationNode
					content=""
					item={location}
				/>
			</Modal>
		</>
	);
};

export const LocationNode: React.FC<LocationNodeProps> = ({
	item, reviews, content, hideLink, hideMap = false, addressAsLink, showRate,
}) => {
	return <div className="row card-panel location">
		<LocationInfo
			item={item}
			showRate={showRate}
			addressAsLink={addressAsLink}
			hideLink={hideLink}
		/>
		{hideMap ? null : <div className="location-inner">
			<SimpleMap address={item.addressEn} />
			<div className="info-block">
				{content && content(item)}
			</div>
		</div>}
		{reviews && <div className="location-reviews">
			{reviews && reviews(item)}
		</div>
		}
	</div>;
};

const Locations: React.FC<IPros> = ({
	locations, containerClassName, content, reviews, hideTitle, hideLink,
}) => {
	if (locations.length === 1) {
		const item = locations[0];
		return (
			<div className={`mb20 locations scroll-top ${containerClassName || ''}`}>
				<div className="location-tab">
					{hideTitle ? null : <h4>
						<Rate
							className="stars"
							averageReviewRate={item.averageReviewRate}
							totalReviewCount={item.totalReviewCount}
						/>
						{getLocationName(item, false)}
					</h4>}
					<LocationNode
						content={content}
						item={item}
						reviews={reviews}
						hideLink={hideLink}
					/>
				</div>
			</div>
		);
	}

	const items: TabsProps['items'] = locations.map((item, index) => {
		return {
			key: index.toString(),
			label: getLocationName(item, false),
			className: 'location-tab',
			children: <LocationNode
				content={content}
				item={item}
				showRate
				reviews={reviews}
			/>,
		};
	});

	return (
		locations.length > 0 ? <div className={`mb20 locations scroll-top ${containerClassName || ''}`}>
			<Tabs type="card" defaultActiveKey="0" items={items} />
		</div> : null
	);
};

export default Locations;
